import React, { useState } from 'react';
import '../CSS/Header.css';
import { useNavigate, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileItem, setExpandedMobileItem] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/admin-dashboard' || location.pathname === '/admin-login';
  
  const handleDropdownEnter = (item: string) => {
    setActiveDropdown(item);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      setExpandedMobileItem(null);
    }
  };

  const toggleMobileItem = (item: string) => {
    setExpandedMobileItem(expandedMobileItem === item ? null : item);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setExpandedMobileItem(null);
  };

  const navigationItems = [
    'About Us',
    'Who We Serve',
    'Current Searches',
    'Our Services',
    'News & Insights',
    'Contact Us'
  ];

  const getSubMenuItems = (item: string) => {
    switch (item) {
      case 'About Us':
        return [
          { label: 'Our History', path: '/our-history' },
          { label: 'Our Team', path: '/teams' }
        ];
      case 'Who We Serve':
        return [
          { label: 'Higher Education', path: '/higher-education' },
          { label: 'Healthcare', path: '/healthcare' }
        ];
      case 'Current Searches':
        return [];
      case 'Our Services':
        return [
          { label: 'Executive Search and Placement Services', path: '/services/executive-search' },
          { label: 'AI Innovation & Academic Modernization', path: '/services/ai-innovation' },
          { label: 'Campus Safety Audits: From Risk to Resilience', path: '/services/campus-safety' },
          { label: 'Executive Coaching for Academic Leaders', path: '/services/executive-coaching' }
        ];
      case 'News & Insights':
        return [
          { label: 'Recent Placements', path: '/news-and-insights/recent-placements' },
          { label: 'E-Newsletters', path: '/news-and-insights/monthly-newsletter' },
          { label: 'Our Success Stories', path: '/news-and-insights/our-success-stories' }
        ];
      case 'Contact Us':
        return [];
      default:
        return [];
    }
  };

  const getDirectPath = (item: string) => {
    switch (item) {
      case 'Current Searches':
        return '/current-searches';
      case 'Contact Us':
        return '/contact-us';
      default:
        return null;
    }
  };

  return (
    <header className="header">
      <div className={`header-container ${isLoginPage ? 'login-page-header' : ''}`}>
        <div className="header-logo">
          <button 
            onClick={() => navigate('/')}
            className="logo-button"
            aria-label="Navigate to home"
          >
            <img 
              src="/assets/Web Version/myersmcrae-logo-light.svg" 
              alt="MyersMcRae Executive Search and Consulting" 
              className="logo-image"
            />
          </button>
        </div>
        
        {!isLoginPage && (
        <nav className="header-nav">
          {navigationItems.map((item) => {
            const subItems = getSubMenuItems(item);
            const hasSubMenu = subItems.length > 0;
            const directPath = getDirectPath(item);
            
            return (
              <div 
                key={item}
                className="nav-item"
                onMouseEnter={hasSubMenu ? () => handleDropdownEnter(item) : undefined}
                onMouseLeave={hasSubMenu ? handleDropdownLeave : undefined}
              >
                {directPath ? (
                  <button 
                    className="nav-link"
                    onClick={() => navigate(directPath)}
                  >
                    {item}
                  </button>
                ) : (
                  <button className="nav-link">
                    {item}
                  </button>
                )}
                {hasSubMenu && activeDropdown === item && (
                  <div 
                    className="dropdown-menu"
                    onMouseEnter={() => handleDropdownEnter(item)}
                    onMouseLeave={handleDropdownLeave}
                  >
                    {subItems.map((subItem) => (
                      <div 
                        key={subItem.label}
                        className="dropdown-item" 
                        onClick={() => {
                          navigate(subItem.path);
                          closeMobileMenu();
                        }}
                      >
                        {subItem.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        )}

        {!isLoginPage && (
        <div className="header-actions">
          <a 
            href="https://app.crelate.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="client-login-btn"
          >
            Client Login
          </a>
        </div>
        )}

        {/* Mobile Hamburger Button */}
        {!isLoginPage && (
        <button 
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        )}
      </div>

      {/* Mobile Menu - Extension of Header */}
      {!isLoginPage && isMobileMenuOpen && (
        <div className="mobile-menu">
          <nav className="mobile-nav">
            {navigationItems.map((item) => {
              const subItems = getSubMenuItems(item);
              const hasSubMenu = subItems.length > 0;
              const directPath = getDirectPath(item);
              
              return (
                <div key={item} className="mobile-nav-item">
                  {hasSubMenu ? (
                    <>
                      <button 
                        className="mobile-nav-link"
                        onClick={() => toggleMobileItem(item)}
                      >
                        {item}
                        <span className={`mobile-chevron ${expandedMobileItem === item ? 'open' : ''}`}>
                          ▼
                        </span>
                      </button>
                      {expandedMobileItem === item && (
                        <div className="mobile-submenu">
                          {subItems.map((subItem) => (
                            <button
                              key={subItem.label}
                              className="mobile-submenu-item"
                              onClick={() => {
                                navigate(subItem.path);
                                closeMobileMenu();
                              }}
                            >
                              {subItem.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      className="mobile-nav-link"
                      onClick={() => {
                        const path = directPath || (item === 'Contact Us' ? '/contact-us' : '/');
                        navigate(path);
                        closeMobileMenu();
                      }}
                    >
                      {item}
                    </button>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="mobile-menu-footer">
            <a 
              href="https://app.crelate.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="mobile-client-login-btn"
              onClick={closeMobileMenu}
            >
              Client Login
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;

